import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useOrg } from '../../../shared/contexts/OrgContext';
import { extractOrgIdFromPath } from '../../../shared/utils/organizationUtils';
import { useMobile } from '../../../shared/hooks/useMobile';
import Logo from '../../../shared/components/ui/Logo';
import { SearchTopBar, AddPlayerModal, EditPlayerModal } from '../components';
import { supabase } from '../../../shared/lib/supabase';
import { playersService } from '../../../shared/services/players';

interface SidebarItem {
  id: string;
  name: string;
  icon: string;
  isActive?: boolean;
}

const PlayersPage: React.FC = () => {
  const { isMobile } = useMobile();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(isMobile);
  const [activeTopNavItem] = useState('players');
  const { signOut } = useAuth();
  const { currentOrganization } = useOrg();
  const location = useLocation();
  const navigate = useNavigate();

  const orgIdFromPath = extractOrgIdFromPath(location.pathname);
  const isPlayersPage =
    location.pathname === `/o/${orgIdFromPath}/roster/players` ||
    location.pathname === `/o/${orgIdFromPath}/roster/players/`;

  const [playerAccess, setPlayerAccess] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [playerAssignments, setPlayerAssignments] = useState<Record<string, any[]>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddPlayerModalOpen, setIsAddPlayerModalOpen] = useState(false);
  const [isEditPlayerModalOpen, setIsEditPlayerModalOpen] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState<any>(null);

  const loadPlayerData = async () => {
      if (!orgIdFromPath) return;

      try {
        setLoading(true);
        const [
          playerAccessRes,
          teamAccessRes,
          tournamentAccessRes,
        ] = await Promise.all([
          supabase
            .from('player_access')
            .select(
              `
                id,
                access_type,
                granted_at,
                active,
                player:player_id (
                  id,
                  full_name,
                  position,
                  nationality,
                  kit_number,
                  profile_image,
                  date_of_birth
                )
              `
            )
            .eq('organisation_id', orgIdFromPath)
            .eq('active', true),
          supabase
            .from('team_access')
            .select('team_id')
            .eq('organisation_id', orgIdFromPath)
            .eq('active', true),
          supabase
            .from('tournament_access')
            .select('tournament_id')
            .eq('organisation_id', orgIdFromPath)
            .eq('is_active', true),
        ]);

        if (playerAccessRes.error) {
          console.error('Error fetching player access:', playerAccessRes.error);
          return;
        }

        if (teamAccessRes.error) {
          console.error('Error fetching team access for players page:', teamAccessRes.error);
          return;
        }

        if (tournamentAccessRes.error) {
          console.error('Error fetching tournament access for players page:', tournamentAccessRes.error);
          return;
        }

        const playerAccessData = playerAccessRes.data || [];
        setPlayerAccess(playerAccessData);

        const accessibleTeamIdValues = (teamAccessRes.data || [])
          .map((row: any) => row.team_id)
          .filter((id: string | number | null | undefined) => id !== null && id !== undefined);
        const accessibleTeamIds = new Set(
          accessibleTeamIdValues.map((id: string | number) => String(id))
        );

        const accessibleTournamentIdValues = (tournamentAccessRes.data || [])
          .map((row: any) => row.tournament_id)
          .filter((id: string | number | null | undefined) => id !== null && id !== undefined);
        const accessibleTournamentIds = new Set(
          accessibleTournamentIdValues.map((id: string | number) => String(id))
        );

        const playerIds = playerAccessData
          .map((pa: any) => pa.player?.id)
          .filter((id: string | undefined | null) => Boolean(id))
          .map((id: string) => String(id));

        if (playerIds.length === 0 || accessibleTeamIdValues.length === 0) {
          setPlayerAssignments({});
          return;
        }

        const { data: squadData, error: squadError } = await supabase
          .from('team_squad')
          .select(
            `
              id,
              player_id,
              team_id,
              tournament_id,
              season_id,
              kit_number,
              position,
              is_active,
              team:team_id ( id, name, logo, organisation_id ),
              tournament:tournament_id ( id, name, logo ),
              season:season_id ( id, name, start_date, end_date )
            `
          )
          .in('player_id', playerIds)
          .in('team_id', accessibleTeamIdValues)
          .eq('is_active', true);

        if (squadError) {
          console.error('Error fetching player squad assignments:', squadError);
          return;
        }

        const assignmentsByPlayer: Record<string, any[]> = {};
        (squadData || []).forEach((assignment: any) => {
          const playerId = assignment.player_id ? String(assignment.player_id) : null;
          const teamId = assignment.team_id ? String(assignment.team_id) : null;
          const tournamentId = assignment.tournament_id ? String(assignment.tournament_id) : null;

          if (!playerId || !teamId) {
            return;
          }

          if (!accessibleTeamIds.has(teamId)) {
            return;
          }

          if (
            tournamentId &&
            accessibleTournamentIds.size > 0 &&
            !accessibleTournamentIds.has(tournamentId)
          ) {
            return;
          }

          if (!assignmentsByPlayer[playerId]) {
            assignmentsByPlayer[playerId] = [];
          }

          assignmentsByPlayer[playerId].push(assignment);
        });

        setPlayerAssignments(assignmentsByPlayer);
      } catch (loadError) {
        console.error('Error loading player data:', loadError);
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    loadPlayerData();
  }, [orgIdFromPath]);

  const sidebarItems: SidebarItem[] = [
    { id: 'for-you', name: 'Teams', icon: '⭐', isActive: activeTopNavItem === 'for-you' },
    { id: 'players', name: 'Players', icon: '👥', isActive: activeTopNavItem === 'players' },
    { id: 'summary', name: 'Summary', icon: '📊', isActive: activeTopNavItem === 'summary' },
    { id: 'recent', name: 'Recent', icon: '🕒', isActive: activeTopNavItem === 'recent' },
    { id: 'dashboard', name: 'Go to Dashboard', icon: '🏠', isActive: false },
  ];

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/');
    } catch {
      navigate('/');
    }
  };

  const handleLogoClick = () => {
    const actualOrgId = currentOrganization?.id || orgIdFromPath || 'default';
    navigate(`/o/${actualOrgId}`);
  };

  const handleSidebarItemClick = (itemId: string) => {
    const actualOrgId = currentOrganization?.id || orgIdFromPath || 'default';

    if (itemId === 'dashboard') {
      navigate(`/o/${actualOrgId}`);
    } else if (itemId === 'for-you') {
      navigate(`/o/${actualOrgId}/roster`, { replace: true });
    } else if (itemId === 'players') {
      navigate(`/o/${actualOrgId}/roster/players`, { replace: true });
    } else if (itemId === 'summary') {
      navigate(`/o/${actualOrgId}/roster/summary`, { replace: true });
    }
  };

  const { myPlayers, accessPlayers } = useMemo(() => {
    if (!orgIdFromPath) {
      return { myPlayers: [], accessPlayers: [] };
    }

    const ownedPlayerIds = new Set<string>();

    Object.entries(playerAssignments).forEach(([playerId, assignments]) => {
      if (
        assignments.some(
          (assignment) =>
            assignment.team?.organisation_id &&
            String(assignment.team.organisation_id) === String(orgIdFromPath)
        )
      ) {
        ownedPlayerIds.add(playerId);
      }
    });

    const ownedPlayerAccess = playerAccess.filter((pa) => {
      const playerId = pa.player?.id ? String(pa.player.id) : null;
      return playerId ? ownedPlayerIds.has(playerId) : false;
    });

    const accessiblePlayerAccess = playerAccess.filter((pa) => {
      const playerId = pa.player?.id ? String(pa.player.id) : null;
      if (!playerId) {
        return true;
      }
      return !ownedPlayerIds.has(playerId);
    });

    return { myPlayers: ownedPlayerAccess, accessPlayers: accessiblePlayerAccess };
  }, [orgIdFromPath, playerAccess, playerAssignments]);

  const orderedPlayers = useMemo(() => {
    const combined: any[] = [];
    const seen = new Set<string>();

    myPlayers.forEach((playerAccessRecord: any) => {
      const playerId = playerAccessRecord.player?.id ? String(playerAccessRecord.player.id) : null;
      if (playerId && !seen.has(playerId)) {
        combined.push(playerAccessRecord);
        seen.add(playerId);
      }
    });

    accessPlayers.forEach((playerAccessRecord: any) => {
      const playerId = playerAccessRecord.player?.id ? String(playerAccessRecord.player.id) : null;
      if (playerId && !seen.has(playerId)) {
        combined.push(playerAccessRecord);
        seen.add(playerId);
      }
    });

    return combined;
  }, [myPlayers, accessPlayers]);

  const filteredPlayers = useMemo(() => {
    if (!searchTerm.trim()) return orderedPlayers;

    const term = searchTerm.toLowerCase();
    return orderedPlayers.filter((record: any) => {
      const player = record.player;
      const playerId = player?.id ? String(player.id) : '';
      const nameMatch = player?.full_name?.toLowerCase().includes(term);
      const positionMatch = player?.position?.toLowerCase().includes(term);
      const assignments = playerAssignments[playerId] || [];
      const teamMatch = assignments.some(
        (assignment: any) =>
          assignment.team?.name?.toLowerCase().includes(term) ||
          assignment.tournament?.name?.toLowerCase().includes(term) ||
          assignment.season?.name?.toLowerCase().includes(term)
      );

      return nameMatch || positionMatch || teamMatch;
    });
  }, [orderedPlayers, playerAssignments, searchTerm]);

  const hasAnyPlayers = orderedPlayers.length > 0;
  const hasPlayers = filteredPlayers.length > 0;

  const handleAddPlayer = () => {
    setIsAddPlayerModalOpen(true);
  };

  const handleEditPlayer = (player: any) => {
    setEditingPlayer(player);
    setIsEditPlayerModalOpen(true);
  };

  const handleUpdatePlayer = async (playerId: string, playerData: Partial<any>) => {
    try {
      await playersService.updatePlayer(playerId, playerData);
      // Reload player data to show updated information
      await loadPlayerData();
    } catch (error) {
      console.error('Error updating player:', error);
      throw error;
    }
  };

  const handleSavePlayer = async (data: {
    playerData: any;
    organisationId: string;
    teamId?: string;
    squadAssignments?: Array<{
      tournamentId: string;
      seasonId: string;
      tournamentName: string;
      seasonName: string;
    }>;
  }) => {
    if (!orgIdFromPath) {
      throw new Error('Organization ID not found');
    }

    try {
      // Step 1: Create player in players table
      const newPlayer = await playersService.createPlayer({
        full_name: data.playerData.full_name,
        position: data.playerData.position || null,
        date_of_birth: data.playerData.date_of_birth || null,
        nationality: data.playerData.nationality || null,
        profile_image: data.playerData.profile_image || null,
        height_cm: data.playerData.height_cm || null,
        weight_kg: data.playerData.weight_kg || null,
        preferred_foot: data.playerData.preferred_foot || null,
        kit_number: data.playerData.kit_number || null,
        bio: data.playerData.bio || null,
        social_media: data.playerData.social_media || null,
        contact_info: data.playerData.contact_info || null,
        is_active: data.playerData.is_active !== false
      });

      // Step 2: Create player_access entry
      const { error: accessError } = await supabase
        .from('player_access')
        .insert({
          player_id: newPlayer.id,
          organisation_id: data.organisationId,
          access_type: 'viewer',
          granted_at: new Date().toISOString(),
          active: true
        });

      if (accessError) {
        console.error('Error creating player access:', accessError);
        throw new Error('Failed to create player access');
      }

      // Step 3: Create multiple team_squad entries if squad assignments are provided
      if (data.teamId && data.squadAssignments && data.squadAssignments.length > 0) {
        const squadEntries = data.squadAssignments.map(assignment => ({
          team_id: data.teamId!,
          player_id: newPlayer.id,
          tournament_id: assignment.tournamentId,
          season_id: assignment.seasonId,
          kit_number: data.playerData.kit_number || null,
          position: data.playerData.position || null,
          is_active: true
        }));

        const { error: squadError } = await supabase
          .from('team_squad')
          .insert(squadEntries);

        if (squadError) {
          console.error('Error creating team squad entries:', squadError);
          // Don't throw - player and access were created successfully
        }
      }

      // Reload player data to show the new player
      await loadPlayerData();
    } catch (error) {
      console.error('Error saving player:', error);
      throw error;
    }
  };

  const getInitials = (name?: string) => {
    if (!name) return '??';
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return '??';
    if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  const renderAssignments = (playerId: string) => {
    const assignments = playerAssignments[playerId] || [];

    if (assignments.length === 0) {
      return (
        <div className="rounded-md border border-dashed border-gray-200 bg-white px-3 py-1.5 text-[11px] text-gray-500">
          No active assignments within your access scope.
        </div>
      );
    }

    return (
      <div className="space-y-1.5">
        {assignments.map((assignment: any) => {
          const labelParts = [
            assignment.team?.name || 'Unknown Team',
            assignment.tournament?.name || null,
            assignment.season?.name || null,
          ]
            .filter(Boolean)
            .join(' • ');

          return (
            <div
              key={assignment.id}
              className="rounded-md border border-gray-100 bg-gray-50 px-3 py-1.5"
            >
              <div className="text-[11px] sm:text-xs font-medium text-gray-700">
                {labelParts || 'Unknown Team'}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {isMobile && isPlayersPage && !isSidebarCollapsed && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsSidebarCollapsed(true)}
        />
      )}

      {isPlayersPage && (
        <div
          className={`bg-white border-r border-gray-200 transition-all duration-300 flex-shrink-0 relative ${
            isMobile
              ? `fixed inset-y-0 left-0 z-50 w-64 ${isSidebarCollapsed ? '-translate-x-full' : 'translate-x-0'}`
              : isSidebarCollapsed
              ? 'w-16'
              : 'w-64'
          }`}
        >
          <div className="p-3 sm:p-4 flex flex-col h-full">
            <div className="flex items-center justify-start mb-3 sm:mb-4">
              <button
                onClick={handleLogoClick}
                className="flex items-center space-x-2 hover:opacity-80 transition-opacity duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-md p-1 min-h-44 min-w-44"
                title="Go to Homepage"
                aria-label="Go to Homepage"
              >
                <Logo size="md" variant={isSidebarCollapsed ? 'mark' : 'full'} className="flex items-center space-x-2" />
              </button>
            </div>

            <div className="absolute top-3 sm:top-4 right-0 transform translate-x-1/2 z-50 pointer-events-auto">
              <button
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                className="w-6 h-6 bg-white border border-gray-200 rounded-full shadow-sm flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors min-h-44 min-w-44"
                title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isSidebarCollapsed ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  )}
                </svg>
              </button>
            </div>

            <div className="flex-shrink-0 mb-1">
              <div className="space-y-1 px-2 sm:px-3">
                {sidebarItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleSidebarItemClick(item.id)}
                    className={`w-full flex items-center ${
                      isSidebarCollapsed ? 'justify-center px-2 py-1.5' : 'space-x-2 sm:space-x-3 px-2 sm:px-3 py-1.5'
                    } rounded-lg transition-colors min-h-44 ${
                      item.isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-sm">{item.icon}</span>
                    {!isSidebarCollapsed && (
                      <span className="text-xs sm:text-sm font-normal">{item.name}</span>
                    )}
                  </button>
                ))}
              </div>

              {!isSidebarCollapsed && (
                <>
                  <div className="my-4 border-t border-gray-200" />
                  <div className="mb-2 px-2 text-[11px] uppercase tracking-wide text-gray-500">Access tables</div>
                  <div className="space-y-1 px-2 sm:px-3">
                    {[
                      { id: 'tournament-access', name: 'Tournament Access', icon: '🏆' },
                      { id: 'team-access', name: 'Team Access', icon: '🧑‍🤝‍🧑' },
                      { id: 'season-access', name: 'Season Access', icon: '🗂️' },
                      { id: 'player-access', name: 'Player Access', icon: '👤' },
                    ].map((t) => (
                      <button
                        key={t.id}
                        onClick={() => navigate(`/o/${orgIdFromPath}/roster/summary#${t.id}`)}
                        className={`w-full flex items-center ${
                          isSidebarCollapsed ? 'justify-center px-2 py-1.5' : 'space-x-2 px-2 sm:px-3 py-1.5'
                        } rounded-lg text-gray-600 hover:bg-gray-50 text-[13px]`}
                      >
                        <span className="text-sm">{t.icon}</span>
                        {!isSidebarCollapsed && <span className="font-normal">{t.name}</span>}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="mt-auto pt-2 sm:pt-3 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={handleLogout}
                className={`w-full flex items-center ${
                  isSidebarCollapsed ? 'justify-center px-2 py-1.5' : 'space-x-2 sm:space-x-3 px-2 sm:px-3 py-1.5'
                } text-gray-700 hover:bg-gray-50 rounded-lg transition-colors min-h-44`}
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
                {!isSidebarCollapsed && <span className="text-xs sm:text-sm font-normal">Logout</span>}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        {isMobile && isPlayersPage && (
          <div className="sticky top-0 z-40 bg-white border-b border-gray-200 px-3 py-2 flex items-center">
            <button
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Toggle menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="ml-2 font-medium text-gray-900">Players</div>
          </div>
        )}
        <main className="flex-1 overflow-y-auto">
          <div className={`${isPlayersPage && isSidebarCollapsed ? 'px-4 py-6' : 'p-6'}`}>
            <SearchTopBar
              title="Players"
              placeholder="Search players..."
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              variant="centered"
              actions={
                <button
                  type="button"
                  onClick={handleAddPlayer}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  + Player
                </button>
              }
            />

            {loading ? (
              <div className="flex justify-center items-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">Loading players...</span>
              </div>
            ) : hasPlayers ? (
              <div className="space-y-3">
                {filteredPlayers.map((playerAccessRecord: any) => {
                  const player = playerAccessRecord.player;
                  const playerId = player?.id ? String(player.id) : '';
                  const assignments = playerId ? renderAssignments(playerId) : null;

                  const primaryAssignment = playerId && playerAssignments[playerId]?.[0];
                  const primaryTeam = primaryAssignment?.team?.name;

                  const summaryParts = [
                    player?.kit_number ? `#${player.kit_number}` : null,
                    player?.position || null,
                    primaryTeam || null,
                  ].filter(Boolean);

                  return (
                    <div
                      key={playerAccessRecord.id}
                      className="rounded-lg border border-gray-200 bg-white p-3 sm:p-3.5 shadow-sm shadow-transparent transition-shadow hover:shadow-md"
                    >
                      <div className="grid grid-cols-[auto,1fr,auto] gap-2.5 sm:gap-3 items-center">
                        <div className="flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 text-xs sm:text-sm font-semibold uppercase text-white">
                          {getInitials(player?.full_name)}
                        </div>
                        <div className="space-y-1">
                          <h3 className="text-sm sm:text-sm font-semibold text-gray-900">
                            {player?.full_name || 'Unknown Player'}
                          </h3>
                          {summaryParts.length > 0 && (
                            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] sm:text-xs text-gray-600">
                              {summaryParts.map((part, index) => (
                                <React.Fragment key={`${playerId}-summary-${index}`}>
                                  {index > 0 && <span>•</span>}
                                  <span>{part}</span>
                                </React.Fragment>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="justify-self-end text-right flex items-center gap-2">
                          <button
                            onClick={() => handleEditPlayer(player)}
                            className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors rounded-md hover:bg-gray-100"
                            title="Edit player"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          {playerAssignments[playerId]?.length ? (
                            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600">
                              {playerAssignments[playerId].length} assignment{playerAssignments[playerId].length === 1 ? '' : 's'}
                            </span>
                          ) : null}
                        </div>
                      </div>

                      {playerAssignments[playerId]?.length ? (
                        <div className="mt-2.5 sm:mt-3">
                          {assignments}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : hasAnyPlayers ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No matches found</h3>
                <p className="text-sm text-gray-500">Try a different search term.</p>
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Players Found</h3>
                <p className="text-sm text-gray-500">You don't have access to any players yet.</p>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Add Player Modal */}
      <AddPlayerModal
        isOpen={isAddPlayerModalOpen}
        onClose={() => setIsAddPlayerModalOpen(false)}
        onSave={handleSavePlayer}
        organisationId={orgIdFromPath || ''}
      />
      <EditPlayerModal
        isOpen={isEditPlayerModalOpen}
        onClose={() => {
          setIsEditPlayerModalOpen(false);
          setEditingPlayer(null);
        }}
        onSave={handleUpdatePlayer}
        player={editingPlayer}
      />
    </div>
  );
};

export default PlayersPage;


